/**
 * Phase 17 — full-screen photo lightbox with zoom + pan.
 *
 * Pure HTML `<dialog>` + CSS transforms. No third-party lib (per
 * `research.md R-007`). Esc / X / backdrop close. Scroll wheel zooms;
 * drag pans when zoomed.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   const [src, setSrc] = useState<string | null>(null);
 *   return (
 *     <>
 *       <img onClick={() => { setSrc(url); setOpen(true); }} ... />
 *       <Lightbox open={open} src={src} alt={...} onClose={() => setOpen(false)} />
 *     </>
 *   );
 *
 * For a swipable set (gallery) pass `images` + `index`; the lightbox
 * shows arrows + keyboard nav.
 */

import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  open: boolean;
  src?: string | null;
  alt?: string;
  /** Optional gallery — pass to enable left/right nav. `index` is the
   * starting position. */
  images?: { src: string; alt?: string }[];
  index?: number;
  onClose: () => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

export function Lightbox({
  open,
  src,
  alt,
  images,
  index = 0,
  onClose,
}: Props) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState<{ x: number; y: number } | null>(null);
  const [currentIndex, setCurrentIndex] = useState(index);

  const gallery = images ?? (src ? [{ src, alt }] : []);
  const active = gallery[currentIndex] ?? null;

  // Reset zoom + pan on open / image change.
  useEffect(() => {
    if (open) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setCurrentIndex(index);
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [open, index]);

  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [currentIndex]);

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "ArrowLeft" && gallery.length > 1) {
        setCurrentIndex((i) => (i - 1 + gallery.length) % gallery.length);
      } else if (e.key === "ArrowRight" && gallery.length > 1) {
        setCurrentIndex((i) => (i + 1) % gallery.length);
      }
    },
    [open, gallery.length],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  if (!open || !active) {
    return (
      <dialog
        ref={dialogRef}
        onClose={onClose}
        className="bg-transparent p-0 backdrop:bg-starz-charcoal/90"
      />
    );
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={(e) => {
        // Click on backdrop closes; click on image does not.
        if (e.target === dialogRef.current) onClose();
      }}
      className="bg-transparent p-0 backdrop:bg-starz-charcoal/90"
    >
      <div className="fixed inset-0 flex items-center justify-center">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full bg-starz-charcoal/70 px-3 py-1 text-starz-off-white"
        >
          ✕
        </button>
        {gallery.length > 1 ? (
          <>
            <button
              type="button"
              aria-label="Previous"
              onClick={() =>
                setCurrentIndex((i) => (i - 1 + gallery.length) % gallery.length)
              }
              className="absolute left-4 z-10 rounded-full bg-starz-charcoal/70 px-3 py-2 text-starz-off-white"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Next"
              onClick={() => setCurrentIndex((i) => (i + 1) % gallery.length)}
              className="absolute right-4 z-10 rounded-full bg-starz-charcoal/70 px-3 py-2 text-starz-off-white"
            >
              ›
            </button>
          </>
        ) : null}
        <img
          src={active.src}
          alt={active.alt ?? ""}
          draggable={false}
          onWheel={(e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.2 : 0.2;
            setZoom((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z + delta)));
          }}
          onMouseDown={(e) => {
            if (zoom <= 1) return;
            setDragging({ x: e.clientX - pan.x, y: e.clientY - pan.y });
          }}
          onMouseMove={(e) => {
            if (!dragging) return;
            setPan({ x: e.clientX - dragging.x, y: e.clientY - dragging.y });
          }}
          onMouseUp={() => setDragging(null)}
          onMouseLeave={() => setDragging(null)}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transition: dragging ? "none" : "transform 120ms ease-out",
            cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
          }}
          className="max-h-[90vh] max-w-[90vw] select-none"
        />
      </div>
    </dialog>
  );
}
